import React from 'react';
import {
  useImpersonateMutation,
  useLogoutMutation,
  useStopImpersonatingMutation,
} from '$/GraphQL/schema/schema';
import { useBizAnalytics } from '$/BizAnalytics';
import { useSocketContext } from '$/components/Socket/SocketContext';
import { useMe } from '$/hooks/useMe';
import { useApolloClient } from '@apollo/client';
import { navigate } from 'gatsby';

export function useImpersonateUser(): {
  impersonateUser: (teamId: string, userId: string) => Promise<void>;
  stopImpersonating: () => Promise<void>;
  logout: () => Promise<void>;
} {
  const me = useMe();
  const { socket } = useSocketContext();
  const client = useApolloClient();
  const [logoutMutation] = useLogoutMutation();

  const [impersonateMutation] = useImpersonateMutation();
  const [stopImpersonatingMutation] = useStopImpersonatingMutation();

  const { analytics } = useBizAnalytics();

  const impersonateUser = React.useCallback(
    async (teamId: string, userId: string) => {
      await impersonateMutation({ variables: { teamId, userId } });
      analytics?.resetIdentity();

      // Hard navigate will close socket and reset client
      document.location.href = '/home';
    },
    [analytics, impersonateMutation]
  );

  const stopImpersonating = React.useCallback(async () => {
    await stopImpersonatingMutation();
    analytics?.resetIdentity();
  }, [analytics, stopImpersonatingMutation]);

  const logout = React.useCallback(async (): Promise<void> => {
    try {
      if (me.impersonatingUser) {
        await stopImpersonating();
        socket?.terminate();

        // Hard navigate will close socket and reset client
        document.location.href = '/ops/teams';
        return;
      }

      await logoutMutation();
      await client.resetStore();
      socket?.terminate();

      // Soft navigate
      navigate('/login');
    } catch (e) {
      console.error('Error logging out', e);
    }
  }, [client, logoutMutation, me.impersonatingUser, socket, stopImpersonating]);

  return { impersonateUser, stopImpersonating, logout };
}
